import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import { Users, BarChart2, CreditCard, MessageSquare, Gem, RefreshCw, ChevronDown, ChevronUp, Check, X, Shield, Search, Filter, UserCheck, Link, Mail, Clock, CheckCircle, XCircle, Inbox, Send, Loader2 } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart2 },
  { id: 'waitlist', label: 'Waitlist', icon: Inbox },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'memberships', label: 'Memberships', icon: Shield },
  { id: 'candidates', label: 'Candidates', icon: UserCheck },
  { id: 'tickets', label: 'Support', icon: MessageSquare },
  { id: 'dedicated', label: 'Dedicated', icon: Gem },
  { id: 'transactions', label: 'Transactions', icon: CreditCard },
];

const STATUS_COLORS = {
  open: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-500',
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
  active: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-500',
  completed: 'bg-blue-100 text-blue-800',
  free: 'bg-gray-100 text-gray-500',
  recon: 'bg-blue-100 text-blue-700',
  intelligence: 'bg-purple-100 text-purple-700',
};

function StatusBadge({ value }) {
  const cls = STATUS_COLORS[value] || 'bg-gray-100 text-gray-500';
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 ${cls}`}>
      {value?.replace(/_/g, ' ')}
    </span>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="bg-white border border-border p-5">
      <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-3xl font-light font-mono ${accent ? 'text-[hsl(var(--primary))]' : ''}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

// ─── Waitlist Tab ─────────────────────────────────────────────────────────────
const WAITLIST_STATUS_COLORS = {
  pending:  'bg-amber-50 text-amber-700 border-amber-200',
  invited:  'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
};

function WaitlistTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND}/api/admin/waitlist`);
      const d = await r.json();
      if (d.success) setEntries(d.data || []);
    } catch { toast.error('Failed to load waitlist'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async (entry) => {
    if (!window.confirm(`Send invitation to ${entry.email}?\n\nThis will create an HR account and email them login credentials.`)) return;
    setInviting(entry.id);
    try {
      const r = await authFetch(`${BACKEND}/api/admin/waitlist/${entry.id}/invite`, { method: 'POST' });
      const d = await r.json();
      if (d.success) {
        toast.success(`Invited ${entry.email}`);
        if (d.message?.includes('password:')) {
          // Show temp password in a copyable toast
          toast.info(d.message, { duration: 15000 });
        }
        load();
      } else { toast.error(d.detail || 'Invite failed'); }
    } catch { toast.error('Invite failed'); }
    finally { setInviting(null); }
  };

  const reject = async (entry) => {
    try {
      await authFetch(`${BACKEND}/api/admin/waitlist/${entry.id}`, {
        method: 'PATCH', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ status: 'rejected' }),
      });
      toast.success('Marked as rejected');
      load();
    } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this entry permanently?')) return;
    try {
      await authFetch(`${BACKEND}/api/admin/waitlist/${id}`, { method: 'DELETE' });
      setEntries(prev => prev.filter(e => e.id !== id));
      toast.success('Removed');
    } catch { toast.error('Failed'); }
  };

  const pending = entries.filter(e => (e.status || 'pending') === 'pending');
  const invited = entries.filter(e => e.status === 'invited');
  const rejected = entries.filter(e => e.status === 'rejected');

  if (loading) return <div className="text-sm text-muted-foreground p-8">Loading waitlist...</div>;

  return (
    <div className="space-y-6" data-testid="waitlist-tab">
      {/* Summary row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Pending', value: pending.length, color: 'text-amber-600' },
          { label: 'Invited', value: invited.length, color: 'text-green-600' },
          { label: 'Rejected', value: rejected.length, color: 'text-red-500' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-border p-4">
            <p className={`text-2xl font-light font-mono ${color}`}>{value}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1">{label}</p>
          </div>
        ))}
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-16 bg-white border border-border">
          <Inbox className="w-8 h-8 text-muted-foreground/25 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">No waitlist entries yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-border overflow-hidden" data-testid="waitlist-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                {['Email', 'Name', 'Company', 'Role', 'Status', 'Date', 'Actions'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => {
                const status = entry.status || 'pending';
                const statusCls = WAITLIST_STATUS_COLORS[status] || 'bg-gray-50 text-gray-500 border-gray-200';
                return (
                  <tr key={entry.id} className="border-b border-border last:border-0 hover:bg-secondary/20" data-testid={`waitlist-row-${entry.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{entry.email}</td>
                    <td className="px-4 py-3 text-xs">{entry.full_name || '—'}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{entry.company || '—'}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{entry.role_type || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border ${statusCls}`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {status === 'pending' && (
                          <>
                            <button
                              onClick={() => invite(entry)}
                              disabled={inviting === entry.id}
                              className="flex items-center gap-1 text-xs text-green-700 border border-green-200 bg-green-50 px-2.5 py-1 hover:bg-green-100 disabled:opacity-50 transition-colors"
                              data-testid={`invite-btn-${entry.id}`}
                            >
                              <Mail className="w-3 h-3" strokeWidth={1.5} />
                              {inviting === entry.id ? 'Sending...' : 'Invite'}
                            </button>
                            <button
                              onClick={() => reject(entry)}
                              className="flex items-center gap-1 text-xs text-red-600 border border-red-200 bg-red-50 px-2.5 py-1 hover:bg-red-100 transition-colors"
                              data-testid={`reject-btn-${entry.id}`}
                            >
                              <X className="w-3 h-3" strokeWidth={1.5} />
                              Reject
                            </button>
                          </>
                        )}
                        {status === 'invited' && (
                          <button
                            onClick={() => invite(entry)}
                            disabled={inviting === entry.id}
                            className="text-xs text-muted-foreground border border-border px-2.5 py-1 hover:border-foreground transition-colors"
                            data-testid={`reinvite-btn-${entry.id}`}
                          >
                            Resend
                          </button>
                        )}
                        <button
                          onClick={() => remove(entry.id)}
                          className="text-muted-foreground/40 hover:text-red-500 transition-colors p-1"
                          data-testid={`delete-waitlist-${entry.id}`}
                        >
                          <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ stats, loading }) {
  if (loading) return <div className="text-sm text-muted-foreground p-8">Loading stats...</div>;
  if (!stats) return <div className="text-sm text-muted-foreground p-8">No data.</div>;
  const s = stats;
  return (
    <div className="space-y-6">
      <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Platform Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={s.total_users} />
        <StatCard label="INTELLIGENCE Active" value={s.active_intelligence} accent />
        <StatCard label="RECON Active" value={s.active_recon} />
        <StatCard label="Free / Inactive" value={s.free_users + s.inactive_members} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Est. Revenue (€)" value={`€${s.total_revenue?.toLocaleString('en-EU', {minimumFractionDigits:2})}`} accent />
        <StatCard label="Candidates" value={s.total_candidates} />
        <StatCard label="Open Tickets" value={s.pending_tickets} sub="Needs attention" />
        <StatCard label="Pending Dedicated" value={s.pending_dedicated} sub="Awaiting review" />
        <StatCard label="Waitlist Pending" value={s.waitlist_pending ?? 0} sub={`${s.waitlist_total ?? 0} total requests`} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-border p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-3">Membership Breakdown</p>
          <div className="space-y-2.5">
            {[
              { label: 'INTELLIGENCE', value: s.active_intelligence, color: 'bg-purple-500' },
              { label: 'RECON', value: s.active_recon, color: 'bg-blue-500' },
              { label: 'Inactive / Expired', value: s.inactive_members, color: 'bg-gray-300' },
              { label: 'Free', value: s.free_users, color: 'bg-gray-200' },
            ].map(({ label, value, color }) => {
              const pct = s.total_users ? Math.round(value / s.total_users * 100) : 0;
              return (
                <div key={label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-mono">{value} ({pct}%)</span>
                  </div>
                  <div className="h-1.5 bg-secondary w-full">
                    <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-white border border-border p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-3">Quick Actions</p>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>→ <span className="font-medium text-foreground">{s.pending_tickets}</span> open support tickets need replies</p>
            <p>→ <span className="font-medium text-foreground">{s.pending_dedicated}</span> dedicated requests awaiting review</p>
            <p>→ <span className="font-medium text-foreground">{s.active_intelligence + s.active_recon}</span> paying members total</p>
          </div>
        </div>
      </div>
      <ScoreRecalcPanel />
    </div>
  );
}

// ─── Score Recalculation Panel ────────────────────────────────────────────────
function ScoreRecalcPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleRecalc = async () => {
    if (!window.confirm('Recompute FashionSpyder scores for ALL active candidates? This uses the updated scoring formula and may take a few seconds.')) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await authFetch(`${BACKEND}/api/admin/recalculate-scores`, { method: 'POST' });
      const d = await r.json();
      setResult(d);
    } catch (e) {
      setResult({ success: false, message: 'Request failed', error: String(e) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-border p-5">
      <p className="text-xs text-muted-foreground uppercase tracking-widest mb-3">Intelligence Scoring</p>
      <p className="text-sm text-muted-foreground mb-4">
        Recompute FashionSpyder authority + overall scores for all active candidates using the latest formula.
        Run this after any formula updates — no Flence re-sync required.
      </p>
      <button
        onClick={handleRecalc}
        disabled={loading}
        data-testid="recalculate-scores-btn"
        className="px-4 py-2 text-xs font-medium border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors disabled:opacity-50 uppercase tracking-wider"
      >
        {loading ? 'Recalculating...' : 'Recalculate All Scores'}
      </button>
      {result && (
        <div className={`mt-3 p-3 text-xs border ${result.success ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
          {result.success ? `✓ Updated ${result.updated}/${result.total} candidates (${result.errors} errors)` : `✗ ${result.message}`}
        </div>
      )}
    </div>
  );
}

// ─── Users Tab ────────────────────────────────────────────────────────────────
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (planFilter) params.set('plan', planFilter);
    const r = await authFetch(`${BACKEND}/api/admin/platform/users?${params}`, {});
    const d = await r.json();
    if (d.success) setUsers(d.data);
    setLoading(false);
  }, [planFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u =>
    !search || u.email?.includes(search) || u.full_name?.toLowerCase().includes(search.toLowerCase()) || u.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  const saveEdit = async () => {
    const r = await authFetch(`${BACKEND}/api/admin/platform/users/${editing}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editData),
    });
    const d = await r.json();
    if (d.success) { toast.success('User updated'); setEditing(null); load(); }
    else toast.error('Update failed');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input data-testid="users-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, company..."
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-border bg-white focus:outline-none focus:ring-1 focus:ring-foreground" />
        </div>
        <select data-testid="users-plan-filter" value={planFilter} onChange={e => setPlanFilter(e.target.value)}
          className="text-sm border border-border px-3 py-1.5 bg-white focus:outline-none">
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="recon">RECON</option>
          <option value="intelligence">INTELLIGENCE</option>
        </select>
        <button onClick={load} className="p-1.5 border border-border hover:bg-secondary text-muted-foreground">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-xs text-muted-foreground">{filtered.length} users</p>
      {loading ? <div className="text-sm text-muted-foreground py-8">Loading...</div> : (
        <div className="bg-white border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">User</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Company</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Plan</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Expires</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Joined</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <React.Fragment key={u.id}>
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border/50 hover:bg-secondary/20">
                    <td className="px-4 py-3">
                      <p className="font-medium text-xs">{u.full_name}</p>
                      <p className="text-[11px] text-muted-foreground">{u.email}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{u.company_name || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge value={u.subscription_plan} /></td>
                    <td className="px-4 py-3"><StatusBadge value={u.subscription_status || 'free'} /></td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                      {u.subscription_expires_at ? new Date(u.subscription_expires_at).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                      {new Date(u.created_at).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`edit-user-${u.id}`}
                        onClick={() => { setEditing(editing === u.id ? null : u.id); setEditData({ subscription_plan: u.subscription_plan, subscription_status: u.subscription_status, notes: u.notes || '' }); }}
                        className="text-xs text-muted-foreground hover:text-foreground underline">
                        Edit
                      </button>
                    </td>
                  </tr>
                  {editing === u.id && (
                    <tr key={`edit-${u.id}`} className="bg-secondary/20">
                      <td colSpan={7} className="px-4 py-3">
                        <div className="flex flex-wrap items-end gap-3">
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Plan</label>
                            <select value={editData.subscription_plan} onChange={e => setEditData(p => ({...p, subscription_plan: e.target.value}))}
                              className="text-sm border border-border px-2 py-1 bg-white focus:outline-none">
                              <option value="free">Free</option>
                              <option value="recon">RECON</option>
                              <option value="intelligence">INTELLIGENCE</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Status</label>
                            <select value={editData.subscription_status || ''} onChange={e => setEditData(p => ({...p, subscription_status: e.target.value}))}
                              className="text-sm border border-border px-2 py-1 bg-white focus:outline-none">
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>
                          <div className="flex-1 min-w-[200px]">
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Admin Notes</label>
                            <input value={editData.notes} onChange={e => setEditData(p => ({...p, notes: e.target.value}))}
                              placeholder="Internal notes..."
                              className="w-full text-sm border border-border px-2 py-1 bg-white focus:outline-none" />
                          </div>
                          <div className="flex gap-2">
                            <button data-testid="save-user-edit" onClick={saveEdit}
                              className="text-xs bg-foreground text-background px-3 py-1.5 hover:opacity-80">Save</button>
                            <button onClick={() => setEditing(null)}
                              className="text-xs border border-border px-3 py-1.5 hover:bg-secondary">Cancel</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Memberships Tab ──────────────────────────────────────────────────────────
function MembershipsTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${BACKEND}/api/admin/platform/memberships?filter=${filter}`, {});
    const d = await r.json();
    if (d.success) setMembers(d.data);
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const now = new Date();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {['all', 'active', 'inactive', 'expired'].map(f => (
          <button key={f} data-testid={`membership-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 uppercase tracking-widest border transition-colors ${filter === f ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground'}`}>
            {f}
          </button>
        ))}
        <button onClick={load} className="p-1.5 border border-border hover:bg-secondary text-muted-foreground ml-auto">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-xs text-muted-foreground">{members.length} members</p>
      {loading ? <div className="text-sm text-muted-foreground py-8">Loading...</div> : (
        <div className="bg-white border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Member</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Plan</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Started</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Expires</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Days Left</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => {
                const exp = m.subscription_expires_at ? new Date(m.subscription_expires_at) : null;
                const daysLeft = exp ? Math.ceil((exp - now) / 86400000) : null;
                const isExpired = daysLeft !== null && daysLeft < 0;
                return (
                  <tr key={m.id} data-testid={`membership-row-${m.id}`} className="border-b border-border/50 hover:bg-secondary/20">
                    <td className="px-4 py-3">
                      <p className="font-medium text-xs">{m.full_name}</p>
                      <p className="text-[11px] text-muted-foreground">{m.email}</p>
                      <p className="text-[11px] text-muted-foreground">{m.company_name}</p>
                    </td>
                    <td className="px-4 py-3"><StatusBadge value={m.subscription_plan} /></td>
                    <td className="px-4 py-3"><StatusBadge value={m.subscription_status} /></td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                      {m.subscription_started_at ? new Date(m.subscription_started_at).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                      {exp ? exp.toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className={`px-4 py-3 text-xs font-mono font-semibold ${isExpired ? 'text-red-600' : daysLeft !== null && daysLeft < 7 ? 'text-amber-600' : 'text-green-600'}`}>
                      {daysLeft !== null ? (isExpired ? `${Math.abs(daysLeft)}d ago` : `${daysLeft}d`) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Support Tickets Tab ──────────────────────────────────────────────────────
function TicketsTab() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [reply, setReply] = useState('');
  const [statusEdit, setStatusEdit] = useState('');

  const load = async () => {
    setLoading(true);
    const r = await authFetch(`${BACKEND}/api/support/admin/tickets`, {});
    const d = await r.json();
    if (d.success) setTickets(d.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateTicket = async (id, data) => {
    const r = await authFetch(`${BACKEND}/api/support/admin/tickets/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const d = await r.json();
    if (d.success) { toast.success('Ticket updated'); load(); setExpanded(null); setReply(''); }
    else toast.error('Failed to update ticket');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{tickets.length} tickets</p>
        <button onClick={load} className="p-1.5 border border-border hover:bg-secondary text-muted-foreground">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground py-8">Loading...</div> : (
        <div className="space-y-2">
          {tickets.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No support tickets yet.</p>}
          {tickets.map(t => (
            <div key={t.id} data-testid={`ticket-${t.id}`} className="bg-white border border-border">
              <button
                onClick={() => { setExpanded(expanded === t.id ? null : t.id); setReply(t.admin_reply || ''); setStatusEdit(t.status); }}
                className="w-full flex items-start gap-4 px-4 py-3 text-left hover:bg-secondary/20 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <StatusBadge value={t.status} />
                    <StatusBadge value={t.category} />
                  </div>
                  <p className="text-sm font-medium mt-1">{t.subject}</p>
                  <p className="text-xs text-muted-foreground">{t.email} · {t.full_name} · {new Date(t.created_at).toLocaleDateString('en-GB')}</p>
                </div>
                {expanded === t.id ? <ChevronUp className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />}
              </button>
              {expanded === t.id && (
                <div className="border-t border-border px-4 py-4 space-y-3 bg-secondary/10">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Message</p>
                    <p className="text-sm leading-relaxed">{t.message}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Status</label>
                      <select value={statusEdit} onChange={e => setStatusEdit(e.target.value)}
                        className="text-sm border border-border px-2 py-1 bg-white focus:outline-none">
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Admin Reply</label>
                    <textarea data-testid={`ticket-reply-${t.id}`}
                      value={reply} onChange={e => setReply(e.target.value)}
                      rows={3} placeholder="Write your reply..."
                      className="w-full text-sm border border-border px-3 py-2 bg-white focus:outline-none resize-none" />
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`ticket-save-${t.id}`}
                      onClick={() => updateTicket(t.id, { status: statusEdit, admin_reply: reply })}
                      className="text-xs bg-foreground text-background px-4 py-1.5 hover:opacity-80">
                      Save &amp; Update
                    </button>
                    <button onClick={() => setExpanded(null)}
                      className="text-xs border border-border px-4 py-1.5 hover:bg-secondary">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Dedicated Requests Tab ───────────────────────────────────────────────────
function DedicatedTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    const r = await authFetch(`${BACKEND}/api/admin/dedicated/requests`, {});
    const d = await r.json();
    if (d.success) setRequests(d.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{requests.length} requests</p>
        <button onClick={load} className="p-1.5 border border-border hover:bg-secondary text-muted-foreground" data-testid="dedicated-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground py-8">Loading...</div> : (
        <div className="space-y-2">
          {requests.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No dedicated hiring requests yet.</p>}
          {requests.map(req => (
            <DedicatedRequestRow
              key={req.id}
              req={req}
              expanded={expanded === req.id}
              onToggle={() => setExpanded(expanded === req.id ? null : req.id)}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DedicatedRequestRow({ req, expanded, onToggle, onChanged }) {
  const user = req.hr_users || {};
  const quoteStatus = req.quote_status || 'none';
  const hasQuote = quoteStatus !== 'none';

  const [monthly, setMonthly] = useState(req.quote_monthly_amount || '');
  const [setup, setSetup] = useState(req.quote_setup_fee || '');
  const [desc, setDesc] = useState(req.quote_description || '');
  const [sending, setSending] = useState(false);

  const sendQuote = async () => {
    if (!monthly || Number(monthly) <= 0) { toast.error('Enter a monthly amount'); return; }
    if (!desc.trim()) { toast.error('Add a service description'); return; }
    setSending(true);
    const r = await authFetch(`${BACKEND}/api/admin/dedicated/${req.id}/quote`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ monthly_amount: Number(monthly), setup_fee: Number(setup) || 0, description: desc.trim() }),
    });
    const d = await r.json();
    setSending(false);
    if (d.success) { toast.success(quoteStatus === 'sent' ? 'Quote updated' : 'Quote sent'); onChanged(); }
    else toast.error(d.detail || 'Failed to send quote');
  };

  const reject = async () => {
    const r = await authFetch(`${BACKEND}/api/admin/dedicated/${req.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'rejected' }),
    });
    const d = await r.json();
    if (d.success) { toast.success('Request rejected'); onChanged(); }
    else toast.error('Action failed');
  };

  const quoteBadge = {
    none: null,
    sent: <span className="text-[10px] uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5">Quote sent</span>,
    accepted: <span className="text-[10px] uppercase bg-green-100 text-green-800 px-1.5 py-0.5">Quote accepted</span>,
    declined: <span className="text-[10px] uppercase bg-zinc-100 text-zinc-600 px-1.5 py-0.5">Declined</span>,
  }[quoteStatus];

  return (
    <div data-testid={`dedicated-req-${req.id}`} className="bg-white border border-border">
      <button onClick={onToggle} className="w-full flex items-start gap-4 px-4 py-3 text-left hover:bg-secondary/20 transition-colors">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <StatusBadge value={req.status} />
            {quoteBadge}
            {req.role_level && <span className="text-[10px] text-muted-foreground uppercase">{req.role_level}</span>}
          </div>
          <p className="text-sm font-medium mt-1">{req.role_title}</p>
          <p className="text-xs text-muted-foreground">{user.email || req.hr_user_id} · {user.company_name} · {new Date(req.created_at).toLocaleDateString('en-GB')}</p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />}
      </button>
      {expanded && (
        <div className="border-t border-border px-4 py-4 space-y-4 bg-secondary/10">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Company</p><p>{req.company_name || user.company_name || '—'}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Salary Range</p><p>{req.expected_salary_min && req.expected_salary_max ? `€${req.expected_salary_min.toLocaleString()} – €${req.expected_salary_max.toLocaleString()}` : '—'}</p></div>
          </div>
          {req.description && (
            <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Brief</p><p className="text-sm whitespace-pre-line">{req.description}</p></div>
          )}
          {req.additional_notes && (
            <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Additional Notes</p><p className="text-sm">{req.additional_notes}</p></div>
          )}

          {/* Quote builder */}
          {req.status !== 'rejected' && req.status !== 'cancelled' && (
            <div className="border border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.04)] p-4 space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-[hsl(var(--primary))] font-semibold flex items-center gap-1.5">
                <Gem className="w-3 h-3" /> {hasQuote ? 'Update Quotation' : 'Create Quotation'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Monthly Retainer (€) *</label>
                  <input type="number" value={monthly} onChange={e => setMonthly(e.target.value)} placeholder="e.g. 1500"
                    className="w-full text-sm border border-border px-3 py-2 bg-white focus:outline-none" data-testid={`quote-monthly-${req.id}`} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">One-time Setup Fee (€)</label>
                  <input type="number" value={setup} onChange={e => setSetup(e.target.value)} placeholder="Optional — 0 if none"
                    className="w-full text-sm border border-border px-3 py-2 bg-white focus:outline-none" data-testid={`quote-setup-${req.id}`} />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Service Scope / Description *</label>
                <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3}
                  placeholder="What's included: dedicated scout, # roles, screening, timeline, deliverables..."
                  className="w-full text-sm border border-border px-3 py-2 bg-white focus:outline-none resize-none" data-testid={`quote-desc-${req.id}`} />
              </div>
              <div className="flex gap-2">
                <button onClick={sendQuote} disabled={sending}
                  className="flex items-center gap-1.5 text-xs bg-[hsl(var(--primary))] text-white px-4 py-2 hover:opacity-90 disabled:opacity-50"
                  data-testid={`send-quote-${req.id}`}>
                  {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  {hasQuote ? 'Update & Resend Quote' : 'Send Quote'}
                </button>
                {req.status === 'pending' && (
                  <button onClick={reject} className="flex items-center gap-1.5 text-xs border border-red-300 text-red-600 px-4 py-2 hover:bg-red-50" data-testid={`reject-dedicated-${req.id}`}>
                    <X className="w-3 h-3" /> Reject
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Ticket thread */}
          <DedicatedThread requestId={req.id} />

          {/* Curated talent */}
          <DedicatedRecommendations requestId={req.id} />
        </div>
      )}
    </div>
  );
}

function DedicatedThread({ requestId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => {
    const r = await authFetch(`${BACKEND}/api/admin/dedicated/${requestId}/messages`, {});
    const d = await r.json();
    if (d.success) setMessages(d.messages || []);
  };
  useEffect(() => { load(); }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    const r = await authFetch(`${BACKEND}/api/admin/dedicated/${requestId}/messages`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: text.trim() }),
    });
    const d = await r.json();
    setSending(false);
    if (d.success) { setText(''); load(); }
    else toast.error(d.detail || 'Failed to send');
  };

  return (
    <div className="border border-border bg-white">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 py-2 border-b border-border flex items-center gap-1.5">
        <MessageSquare className="w-3 h-3" /> Client Updates
      </p>
      <div className="max-h-56 overflow-y-auto p-3 space-y-2" data-testid={`dedicated-thread-${requestId}`}>
        {messages.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No messages yet. Send your client an update.</p>}
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-3 py-2 text-xs ${m.sender_role === 'admin' ? 'bg-foreground text-background' : 'bg-secondary text-foreground'}`}>
              <p className="whitespace-pre-line">{m.message}</p>
              <p className={`text-[9px] mt-1 ${m.sender_role === 'admin' ? 'text-background/50' : 'text-muted-foreground'}`}>
                {m.sender_role === 'admin' ? 'You' : 'Client'} · {new Date(m.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 p-2 border-t border-border">
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Send an update to the client..." className="flex-1 text-sm border border-border px-3 py-2 focus:outline-none"
          data-testid={`admin-msg-input-${requestId}`} />
        <button onClick={send} disabled={sending} className="bg-foreground text-background px-3 py-2 disabled:opacity-50" data-testid={`admin-msg-send-${requestId}`}>
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ─── Admin: Curated Talent Recommendations ────────────────────────────────────
function DedicatedRecommendations({ requestId }) {
  const [recos, setRecos] = useState([]);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const r = await authFetch(`${BACKEND}/api/admin/dedicated/${requestId}/recommendations`, {});
    const d = await r.json();
    if (d.success) setRecos(d.recommendations || []);
  };
  useEffect(() => { load(); }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async () => {
    setSearching(true);
    const r = await authFetch(`${BACKEND}/api/admin/dedicated/candidate-search?q=${encodeURIComponent(q)}`, {});
    const d = await r.json();
    setSearching(false);
    if (d.success) setResults(d.candidates || []);
  };

  const send = async (candidateId) => {
    const r = await authFetch(`${BACKEND}/api/admin/dedicated/${requestId}/recommend`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ candidate_id: candidateId, note: note.trim() }),
    });
    const d = await r.json();
    if (d.success) { toast.success('Candidate sent to client'); setNote(''); setSelected(null); load(); }
    else toast.error(d.error || d.detail || 'Failed to send');
  };

  const statusChip = (s) => ({
    new: <span className="text-[9px] uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5">Sent</span>,
    interested: <span className="text-[9px] uppercase bg-green-100 text-green-800 px-1.5 py-0.5">Interested</span>,
    passed: <span className="text-[9px] uppercase bg-zinc-100 text-zinc-500 px-1.5 py-0.5">Passed</span>,
  }[s] || null);

  return (
    <div className="border border-border bg-white">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 py-2 border-b border-border flex items-center gap-1.5">
        <UserCheck className="w-3 h-3" /> Recommend Talent
      </p>
      <div className="p-3 space-y-3" data-testid={`dedicated-reco-${requestId}`}>
        {/* Already sent */}
        {recos.length > 0 && (
          <div className="space-y-1.5">
            {recos.map(rec => (
              <div key={rec.id} className="flex items-center gap-2 text-xs border border-border px-2 py-1.5">
                {rec.candidate?.photo_url
                  ? <img src={rec.candidate.photo_url} alt="" className="w-7 h-7 object-cover" />
                  : <div className="w-7 h-7 bg-secondary" />}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{rec.candidate?.full_name || `#${rec.candidate_id}`}</p>
                  <p className="text-muted-foreground truncate">{rec.candidate?.core_role || '—'}</p>
                </div>
                {statusChip(rec.client_status)}
              </div>
            ))}
          </div>
        )}

        {/* Search + send */}
        <div className="flex gap-2">
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()}
            placeholder="Search talent by name or role..." className="flex-1 text-sm border border-border px-3 py-2 focus:outline-none"
            data-testid={`reco-search-input-${requestId}`} />
          <button onClick={search} disabled={searching} className="bg-foreground text-background px-3 py-2 disabled:opacity-50" data-testid={`reco-search-btn-${requestId}`}>
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </div>

        {results.length > 0 && (
          <div className="border border-border max-h-48 overflow-y-auto divide-y divide-border">
            {results.map(c => (
              <div key={c.id} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                {c.photo_url ? <img src={c.photo_url} alt="" className="w-7 h-7 object-cover" /> : <div className="w-7 h-7 bg-secondary" />}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{c.full_name}</p>
                  <p className="text-muted-foreground truncate">{c.core_role} · {c.location_city || '—'}</p>
                </div>
                {selected === c.id ? (
                  <div className="flex items-center gap-1">
                    <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)"
                      className="text-xs border border-border px-2 py-1 w-32 focus:outline-none" data-testid={`reco-note-${c.id}`} />
                    <button onClick={() => send(c.id)} className="bg-[hsl(var(--primary))] text-white px-2 py-1" data-testid={`reco-confirm-${c.id}`}>
                      <Send className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => { setSelected(c.id); setNote(''); }} className="text-[10px] uppercase border border-border px-2 py-1 hover:bg-secondary" data-testid={`reco-add-${c.id}`}>
                    Send
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Transactions Tab ─────────────────────────────────────────────────────────
function TransactionsTab() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await authFetch(`${BACKEND}/api/admin/platform/transactions`, {});
    const d = await r.json();
    if (d.success) setTxns(d.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const total = txns.filter(t => t.payment_status === 'paid').reduce((s, t) => s + parseFloat(t.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{txns.length} transactions</p>
          <p className="text-sm font-semibold mt-0.5">Total collected: <span className="font-mono text-[hsl(var(--primary))]">€{total.toLocaleString('en-EU', {minimumFractionDigits:2})}</span></p>
        </div>
        <button onClick={load} className="p-1.5 border border-border hover:bg-secondary text-muted-foreground">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground py-8">Loading...</div> : (
        <div className="bg-white border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Date</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">User</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Plan</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Amount</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {txns.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground text-sm">No transactions yet.</td></tr>
              )}
              {txns.map(t => (
                <tr key={t.id} data-testid={`txn-${t.id}`} className="border-b border-border/50 hover:bg-secondary/20">
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                    {new Date(t.created_at).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-4 py-3 text-xs">{t.email || '—'}</td>
                  <td className="px-4 py-3"><StatusBadge value={t.plan_name?.toLowerCase() || t.plan_id} /></td>
                  <td className="px-4 py-3 text-xs font-mono font-semibold">
                    {t.currency?.toUpperCase() || 'EUR'} {parseFloat(t.amount || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3"><StatusBadge value={t.payment_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ─── Candidates Tab ───────────────────────────────────────────────────────────
function CandidatesTab() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [portalLink, setPortalLink] = useState({});
  const [linkLoading, setLinkLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeOnly) params.set('active_only', 'true');
    const r = await authFetch(`${BACKEND}/api/admin/platform/candidates?${params}`, {});
    const d = await r.json();
    if (d.success) setCandidates(d.data);
    setLoading(false);
  }, [activeOnly]);

  useEffect(() => { load(); }, [load]);

  const filtered = candidates.filter(c =>
    !search ||
    (c.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.core_role || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.location_city || '').toLowerCase().includes(search.toLowerCase())
  );

  const saveEdit = async (id) => {
    const r = await authFetch(`${BACKEND}/api/admin/platform/candidates/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editData),
    });
    const d = await r.json();
    if (d.success) { toast.success('Candidate updated'); setEditing(null); load(); }
    else toast.error('Update failed');
  };

  const toggleActive = async (c) => {
    const r = await authFetch(`${BACKEND}/api/admin/platform/candidates/${c.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !c.is_active }),
    });
    const d = await r.json();
    if (d.success) { toast.success(c.is_active ? 'Profile deactivated' : 'Profile activated'); load(); }
  };

  const generateLink = async (id) => {
    setLinkLoading(id);
    const r = await authFetch(`${BACKEND}/api/admin/platform/candidates/${id}/portal-link`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ days: 30 }),
    });
    const d = await r.json();
    if (d.success) {
      setPortalLink(prev => ({ ...prev, [id]: d.portal_url }));
      toast.success('Portal link generated');
    } else toast.error('Failed to generate link');
    setLinkLoading(null);
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input data-testid="candidates-search"
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name, role, city..."
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-border bg-white focus:outline-none focus:ring-1 focus:ring-foreground" />
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} className="w-3 h-3" />
          Active only
        </label>
        <button onClick={load} className="p-1.5 border border-border hover:bg-secondary text-muted-foreground ml-auto">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-xs text-muted-foreground">{filtered.length} candidates</p>
      {loading ? <div className="text-sm text-muted-foreground py-8">Loading...</div> : (
        <div className="space-y-2">
          {filtered.map(c => (
            <div key={c.id} data-testid={`candidate-row-${c.id}`} className={`bg-white border ${c.is_active ? 'border-border' : 'border-border/50 opacity-60'}`}>
              <div className="flex items-center gap-4 px-4 py-3">
                {c.photo_url ? (
                  <img src={c.photo_url} alt={c.full_name} className="w-9 h-9 object-cover border border-border flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 bg-secondary border border-border flex items-center justify-center text-xs font-medium flex-shrink-0">
                    {(c.full_name || '?')[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{c.full_name}</p>
                  <p className="text-[11px] text-muted-foreground">{c.core_role} · {c.seniority} · {c.location_city}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusBadge value={c.is_active ? 'active' : 'inactive'} />
                  {c.overall_ranking > 0 && (
                    <span className="text-[10px] font-mono text-muted-foreground">{c.overall_ranking?.toFixed(1)}</span>
                  )}
                  <button data-testid={`toggle-candidate-${c.id}`}
                    onClick={() => toggleActive(c)}
                    className={`text-[10px] px-2 py-0.5 border transition-colors ${c.is_active ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
                    {c.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button data-testid={`edit-candidate-${c.id}`}
                    onClick={() => {
                      setEditing(editing === c.id ? null : c.id);
                      setEditData({ core_role: c.core_role || '', seniority: c.seniority || '', availability: c.availability || '' });
                    }}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline">
                    Edit
                  </button>
                </div>
              </div>

              {/* Portal link row */}
              <div className="border-t border-border/30 px-4 py-2 flex items-center gap-3 bg-secondary/10">
                <Link className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                {portalLink[c.id] ? (
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-[10px] text-muted-foreground font-mono truncate flex-1">{portalLink[c.id]}</span>
                    <button onClick={() => copyLink(portalLink[c.id])}
                      className="text-[10px] text-[hsl(var(--primary))] underline flex-shrink-0">Copy</button>
                  </div>
                ) : (
                  <button data-testid={`gen-portal-link-${c.id}`}
                    onClick={() => generateLink(c.id)}
                    disabled={linkLoading === c.id}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline disabled:opacity-50">
                    {linkLoading === c.id ? 'Generating...' : 'Generate candidate portal link'}
                  </button>
                )}
              </div>

              {/* Edit form */}
              {editing === c.id && (
                <div className="border-t border-border px-4 py-3 bg-secondary/10">
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Role</label>
                      <input value={editData.core_role} onChange={e => setEditData(p => ({...p, core_role: e.target.value}))}
                        className="text-sm border border-border px-2 py-1 bg-white focus:outline-none w-44" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Seniority</label>
                      <select value={editData.seniority} onChange={e => setEditData(p => ({...p, seniority: e.target.value}))}
                        className="text-sm border border-border px-2 py-1 bg-white focus:outline-none">
                        {['Junior','Mid-level','Senior','Lead','Director','VP'].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Availability</label>
                      <select value={editData.availability} onChange={e => setEditData(p => ({...p, availability: e.target.value}))}
                        className="text-sm border border-border px-2 py-1 bg-white focus:outline-none">
                        {['Immediately','1 month','3 months','Not looking'].map(a => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`save-candidate-${c.id}`} onClick={() => saveEdit(c.id)}
                        className="text-xs bg-foreground text-background px-3 py-1.5 hover:opacity-80">Save</button>
                      <button onClick={() => setEditing(null)}
                        className="text-xs border border-border px-3 py-1.5 hover:bg-secondary">Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Main AdminPanel ──────────────────────────────────────────────────────────
export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    authFetch(`${BACKEND}/api/admin/platform/stats`, {})
      .then(r => r.json())
      .then(d => { if (d.success) setStats(d.stats); setStatsLoading(false); })
      .catch(() => setStatsLoading(false));
  }, []);

  return (
    <div data-testid="admin-panel" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-light tracking-tight">Admin Panel</h1>
          <p className="text-xs text-muted-foreground mt-0.5 uppercase tracking-widest">Platform Management</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground border border-border px-3 py-1.5 bg-white">
          <Shield className="w-3 h-3" />
          <span>Admin Access</span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center border-b border-border gap-0 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} data-testid={`admin-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold uppercase tracking-widest border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && <OverviewTab stats={stats} loading={statsLoading} />}
        {activeTab === 'waitlist' && <WaitlistTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'memberships' && <MembershipsTab />}
        {activeTab === 'candidates' && <CandidatesTab />}
        {activeTab === 'tickets' && <TicketsTab />}
        {activeTab === 'dedicated' && <DedicatedTab />}
        {activeTab === 'transactions' && <TransactionsTab />}
      </div>
    </div>
  );
}
